import {
  createAgent,
  deleteAgentById,
  fetchAgentById,
  fetchAgentKnowledgeBases,
  fetchAgentTemplates,
  fetchAgentTools,
  fetchAllAgents,
  fetchAllVoices,
  fetchLLMModels,
  fetchUnassociatedAgents,
  linkKnowledgeBaseToAgent,
  linkToolToAgent,
  unlinkKnowledgeBaseFromAgent,
  unlinkToolFromAgent,
  updateAgentById,
  updateAgentStatus,
} from '@/services/agent.service';

import { queryKeys } from '@/query/query-keys';
import {
  notifyMutationErrorWithOptions,
  notifyMutationSuccessWithOptions,
} from '@/query/query-notifications';

import type { AgentUpdateFormValues } from '@/schemas/agent.schema';

import type {
  Agent,
  AgentDetailResponse,
  AgentFilterParams,
  AgentKnowledgeBase,
  AgentTemplate,
  AgentTool,
  AgentUpdateResponseSchema,
} from '@/types/agent.type';
import type { PaginatedResponse, QueryOptions } from '@/types/common.type';
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';

export const useAgentsQuery = (
  params: AgentFilterParams,
  options?: Omit<UseQueryOptions<PaginatedResponse<Agent>, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.agents.list(
      params.page || 1,
      params.page_size,
      params.query || '',
      params.filters || {}
    ),
    queryFn: () => fetchAllAgents(params),
    ...options,
  });
};

export const useCreateAgentMutation = (options?: QueryOptions<string>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.agents.mutations.create,
    mutationFn: createAgent,
    onSuccess: agentId => {
      notifyMutationSuccessWithOptions(
        options,
        'Agent created',
        'A new agent was created successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      options?.onSuccess?.(agentId);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Agent creation failed', error);
      options?.onError?.(error);
    },
  });
};

export const useDeleteAgentMutation = (options?: QueryOptions<null>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.agents.mutations.delete,
    mutationFn: deleteAgentById,
    onMutate: async agentId => {
      await queryClient.cancelQueries({ queryKey: queryKeys.agents.all });
      const previousData = queryClient.getQueryData<PaginatedResponse<Agent>>(queryKeys.agents.all);

      queryClient.setQueryData(
        queryKeys.agents.all,
        (old: PaginatedResponse<Agent> | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter(agent => agent.id !== agentId),
          };
        }
      );

      return { previousData };
    },
    onError: (error, _variables, onMutateResult, _context) => {
      if (onMutateResult?.previousData) {
        queryClient.setQueryData(queryKeys.agents.all, onMutateResult.previousData);
      }
      notifyMutationErrorWithOptions(options, 'Agent deletion failed', error);
      options?.onError?.(error);
    },
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Agent deleted',
        'The agent was deleted successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      options?.onSuccess?.(null);
    },
  });
};

export const useAgentDetailQuery = (
  agentId: string,
  options?: Omit<UseQueryOptions<AgentDetailResponse, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.agents.detail(agentId),
    queryFn: () => fetchAgentById(agentId),
    ...options,
  });
};

export const useUpdateAgentMutation = (options?: QueryOptions<AgentUpdateResponseSchema>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.agents.mutations.update,
    mutationFn: ({ agentId, data }: { agentId: number; data: Partial<AgentUpdateFormValues> }) =>
      updateAgentById(agentId, data),
    onSuccess: data => {
      notifyMutationSuccessWithOptions(
        options,
        'Agent updated',
        'Agent details were updated successfully.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.detail(data.canonical_name) });
      options?.onSuccess?.(data);
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Agent update failed', error);
      options?.onError?.(error);
    },
  });
};

export const useAgentVoiceQuery = () => {
  return useQuery({
    queryKey: queryKeys.agents.voices,
    queryFn: fetchAllVoices,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useLLMModelsQuery = () => {
  return useQuery({
    queryKey: queryKeys.agents.llmModels,
    queryFn: fetchLLMModels,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useAgentTemplatesQuery = (
  options?: Omit<UseQueryOptions<AgentTemplate[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.agents.templates,
    queryFn: fetchAgentTemplates,
    staleTime: 1000 * 60 * 10, // 10 minutes
    ...options,
  });
};

export const useUpdateAgentStatusMutation = (options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.agents.mutations.updateStatus,
    mutationFn: ({ agentId, status }: { agentId: number; status: string }) =>
      updateAgentStatus(agentId, status),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Agent status updated',
        'Agent status has been updated.'
      );
      queryClient.invalidateQueries({ queryKey: queryKeys.agents.detailAll });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Agent status update failed', error);
      options?.onError?.(error);
    },
  });
};

export const useUnassociatedAgentsQuery = () => {
  return useQuery({
    queryKey: queryKeys.agents.unassociated,
    queryFn: () => fetchUnassociatedAgents(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Fetch knowledge bases for an agent.
 * @param linked - true = only linked, false = only unlinked, undefined = all
 */
export const useAgentKnowledgeBasesQuery = (
  agentId: number,
  linked?: boolean,
  options?: Omit<UseQueryOptions<AgentKnowledgeBase[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.agents.knowledgeBases(agentId, linked),
    queryFn: () => fetchAgentKnowledgeBases(agentId, linked),
    ...options,
  });
};

/**
 * Link a knowledge base to an agent
 */
export const useLinkKnowledgeBaseMutation = (agentId: number, options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.agents.mutations.linkKnowledgeBase,
    mutationFn: (kbId: number) => linkKnowledgeBaseToAgent(agentId, kbId),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Knowledge base linked',
        'The knowledge base has been linked to this agent.'
      );
      // Invalidate all agent KB queries (linked, unlinked, and all)
      queryClient.invalidateQueries({
        queryKey: ['agent-knowledge-bases', agentId],
      });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Failed to link knowledge base', error);
      options?.onError?.(error);
    },
  });
};

/**
 * Unlink a knowledge base from an agent
 */
export const useUnlinkKnowledgeBaseMutation = (agentId: number, options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.agents.mutations.unlinkKnowledgeBase,
    mutationFn: (kbId: number) => unlinkKnowledgeBaseFromAgent(agentId, kbId),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Knowledge base unlinked',
        'The knowledge base has been removed from this agent.'
      );
      // Invalidate all agent KB queries (linked, unlinked, and all)
      queryClient.invalidateQueries({
        queryKey: ['agent-knowledge-bases', agentId],
      });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Failed to unlink knowledge base', error);
      options?.onError?.(error);
    },
  });
};

/**
 * Fetch tools for an agent.
 * @param linked - true = only linked, false = only unlinked, undefined = all
 */
export const useAgentToolsQuery = (
  agentId: number,
  linked?: boolean,
  options?: Omit<UseQueryOptions<AgentTool[], Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: queryKeys.agents.tools(agentId, linked),
    queryFn: () => fetchAgentTools(agentId, linked),
    ...options,
  });
};

/**
 * Link a tool to an agent
 */
export const useLinkToolMutation = (agentId: number, options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.agents.mutations.linkTool,
    mutationFn: (toolId: string) => linkToolToAgent(agentId, toolId),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Tool linked',
        'The tool has been linked to this agent.'
      );
      queryClient.invalidateQueries({
        queryKey: ['agent-tools', agentId],
      });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Failed to link tool', error);
      options?.onError?.(error);
    },
  });
};

/**
 * Unlink a tool from an agent
 */
export const useUnlinkToolMutation = (agentId: number, options?: QueryOptions<void>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: queryKeys.agents.mutations.unlinkTool,
    mutationFn: (toolId: string) => unlinkToolFromAgent(agentId, toolId),
    onSuccess: () => {
      notifyMutationSuccessWithOptions(
        options,
        'Tool unlinked',
        'The tool has been removed from this agent.'
      );
      queryClient.invalidateQueries({
        queryKey: ['agent-tools', agentId],
      });
      options?.onSuccess?.();
    },
    onError: error => {
      notifyMutationErrorWithOptions(options, 'Failed to unlink tool', error);
      options?.onError?.(error);
    },
  });
};
